import React from 'react';
import PropTypes from 'prop-types';
import { ClusterLayout } from '@mc/wink';
import useSmsMultiEverythingExperiment from '../../../Sms/hooks/useSmsMultiEverythingExperiment';
import {
  getCountryUnicodeFlag,
  getDefaultCountryProgram,
  getSmsProgramDataLegalText,
} from '../../utils/helpers';
import TextField from '../BaseComponent/TextField';
import FieldContainer from '../BaseComponent/FieldContainer';
import { usePopupEditorText } from '../../../../SignupForms/hooks/usePopupEditorText';

const SMSPhoneSection = ({
  legalText,
  smsProgramData,
  required,
  defaultCountryCode,
}) => {
  const { shouldShowMultiEverythingComponents } =
    useSmsMultiEverythingExperiment();
  const isMultiCountryEnabled = shouldShowMultiEverythingComponents;
  const requiredLegal = getSmsProgramDataLegalText(
    smsProgramData,
    defaultCountryCode,
    legalText,
    isMultiCountryEnabled,
    true,
  );
  const optionalLegal = getSmsProgramDataLegalText(
    smsProgramData,
    defaultCountryCode,
    legalText,
    isMultiCountryEnabled,
    false,
  );
  const divRegex = /<\/?[div][^>]*>/gi;
  const fullAnchorRegex = /<a.*?<\/a>/g;
  const anchorRegex = /<a href="(.*?)" target="(.*?)">(.*?)<\/a>/;
  const requiredLegalText = requiredLegal
    ?.replace(divRegex, '')
    ?.replace(fullAnchorRegex, '')
    ?.slice(0, -1);
  const optionalLegalText = optionalLegal
    ?.replace(divRegex, '')
    ?.replace(fullAnchorRegex, '')
    ?.slice(0, -1);
  const anchorMatches = requiredLegal?.match(anchorRegex); //the anchor stays the same for required and optional
  if (!anchorMatches) {
    return '';
  }
  return (
    <div
      className="mc-sms-phone-group"
      style={{ display: 'flex', gap: '15px', alignItems: 'center' }}
    >
      <label style={{ alignItems: 'center' }}>
        {required && (
          <input
            type="checkbox"
            name="mc-SMSPHONE-ack"
            id="mc-SMSPHONE-ack"
            value="true"
            style={{ marginRight: '5px', verticalAlign: 'text-bottom' }}
          />
        )}
        {required ? requiredLegalText : optionalLegalText}
        <span>
          <a href={anchorMatches[1]} target={anchorMatches[2]}>
            {anchorMatches[3]}
          </a>
        </span>
        .
      </label>
    </div>
  );
};

SMSPhoneSection.propTypes = {
  defaultCountryCode: PropTypes.string,
  legalText: PropTypes.any.isRequired,
  required: PropTypes.bool,
  smsProgramData: PropTypes.array,
};

const returnCountryCode = (
  smsProgramData,
  altText,
  defaultCountryCode,
  isMultiCountryEnabled,
) => {
  return (
    <>
      {isMultiCountryEnabled ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span>
            {getCountryUnicodeFlag(
              getDefaultCountryProgram(defaultCountryCode, smsProgramData)
                ?.countryCode,
            )}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
            >
              <path
                d="M8.5096 10.6786C8.4219 10.6786 8.33506 10.6613 8.25407 10.6277C8.17307 10.594 8.09951 10.5448 8.0376 10.4826L4.04293 6.47665C3.98099 6.41466 3.93186 6.34109 3.89836 6.26012C3.86485 6.17915 3.84763 6.09237 3.84766 6.00475C3.84769 5.91712 3.86498 5.83035 3.89854 5.74941C3.9321 5.66846 3.98128 5.59492 4.04326 5.53298C4.10525 5.47104 4.17882 5.42192 4.25979 5.38841C4.34076 5.35491 4.42754 5.33768 4.51517 5.33771C4.60279 5.33774 4.68956 5.35503 4.7705 5.38859C4.85145 5.42215 4.92499 5.47133 4.98693 5.53331L8.51093 9.06665L12.0443 5.54331C12.1694 5.4184 12.3391 5.34833 12.516 5.34851C12.6928 5.3487 12.8623 5.41913 12.9873 5.54431C13.1122 5.6695 13.1823 5.83917 13.1821 6.01602C13.1819 6.19287 13.1114 6.3624 12.9863 6.48731L8.98026 10.484C8.85532 10.6086 8.68607 10.6786 8.5096 10.6786Z"
                fill="#21262A"
              />
            </svg>
            {
              getDefaultCountryProgram(defaultCountryCode, smsProgramData)
                ?.countryCallingCode
            }
          </span>
        </div>
      ) : (
        <ClusterLayout>
          <img
            src={smsProgramData?.imgSrc}
            alt={altText}
            width="28"
            height="28"
          ></img>
        </ClusterLayout>
      )}
    </>
  );
};

const SmsPhone = ({
  field,
  label,
  propsFromForm,
  textClassName,
  destination,
  ...props
}) => {
  const { sms_flag_alt_text } = usePopupEditorText();

  const { shouldShowMultiEverythingComponents } =
    useSmsMultiEverythingExperiment();
  const isMultiCountryEnabled = shouldShowMultiEverythingComponents;

  return (
    <FieldContainer>
      <TextField
        field={field}
        label={label}
        propsFromForm={propsFromForm}
        textClassName={textClassName}
        type="tel"
        prefixText={
          destination.smsProgramData
            ? returnCountryCode(
                isMultiCountryEnabled
                  ? destination.smsProgramData
                  : destination.smsProgramData[0],
                sms_flag_alt_text,
                destination.defaultCountryCode,
                isMultiCountryEnabled,
              )
            : ''
        }
        {...props}
      />
      <SMSPhoneSection
        defaultCountryCode={destination.defaultCountryCode}
        legalText={destination.legal}
        smsProgramData={destination.smsProgramData}
        required={field.isRequired}
      />
    </FieldContainer>
  );
};

SmsPhone.propTypes = {
  destination: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
  label: PropTypes.string,
  propsFromForm: PropTypes.object,
  textClassName: PropTypes.string,
};

export default SmsPhone;
